import React, { useEffect, useRef, useCallback } from 'react';

// Field configuration
const STAR_COUNT = 260;
const MIN_SPEED = 0.004; // px per ms, at depth 0
const MAX_SPEED = 0.026; // px per ms, at depth 1 (nearest)
const BACKGROUND_COLOR = '#0f1218'; // prometheus dark

// A long pause (backgrounded tab) freezes requestAnimationFrame while wall-clock
// keeps running. Clamping the per-frame delta stops the whole field from
// teleporting off-screen on return instead of resuming where it left off.
const MAX_DELTA_MS = 50;

// Subtle stellar palette — mostly blue-white/white with a few warm outliers.
// Weights bias hard toward neutral so the colour reads as variety, not confetti.
const STAR_COLORS: Array<{ rgb: [number, number, number]; weight: number }> = [
  { rgb: [205, 224, 255], weight: 0.34 }, // blue-white
  { rgb: [255, 255, 255], weight: 0.34 }, // white
  { rgb: [255, 244, 230], weight: 0.16 }, // warm white
  { rgb: [255, 214, 166], weight: 0.1 },  // amber
  { rgb: [255, 200, 200], weight: 0.06 }, // faint rose
];

// One pre-rendered glow sprite per colour. Drawing a cached radial-gradient
// bitmap is far cheaper than building a gradient per star per frame, and in
// 'lighter' blend mode overlapping sprites bloom like real stars.
const SPRITE_RADIUS = 24; // sprite is 48x48; stars scale it down when drawn

interface Star {
  x: number;
  y: number;
  radius: number;      // drawn core radius in px
  speed: number;       // px per ms
  brightness: number;  // 0..1 base alpha
  spriteIndex: number;
  twinklePhase: number;
  twinkleHz: number;
  twinkleAmp: number;  // 0..1, how much this star shimmers
}

const buildSprite = (rgb: [number, number, number]): HTMLCanvasElement => {
  const size = SPRITE_RADIUS * 2;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  const [r, g, b] = rgb;

  const grad = ctx.createRadialGradient(
    SPRITE_RADIUS, SPRITE_RADIUS, 0,
    SPRITE_RADIUS, SPRITE_RADIUS, SPRITE_RADIUS,
  );
  // Bright near-white core -> the star's colour -> transparent halo
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.18, `rgba(${r},${g},${b},0.95)`);
  grad.addColorStop(0.5, `rgba(${r},${g},${b},0.25)`);
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`);

  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  return c;
};

const pickWeighted = (): number => {
  const roll = Math.random();
  let acc = 0;
  for (let i = 0; i < STAR_COLORS.length; i++) {
    acc += STAR_COLORS[i].weight;
    if (roll <= acc) return i;
  }
  return 0;
};

export const BackgroundStars: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const spritesRef = useRef<HTMLCanvasElement[]>([]);
  const starsRef = useRef<Star[]>([]);
  const animationFrameRef = useRef<number>();
  const lastFrameTimeRef = useRef<number>(0);
  const elapsedRef = useRef<number>(0); // seconds of "animation time", pause-safe
  const reducedMotionRef = useRef<boolean>(false);

  // Depth (0..1) couples size, drift speed and brightness so nearer stars are
  // bigger, faster and brighter — cheap parallax.
  const initializeStars = useCallback((width: number, height: number): Star[] => {
    const stars: Star[] = [];
    for (let i = 0; i < STAR_COUNT; i++) {
      const depth = Math.pow(Math.random(), 1.6); // skew toward distant/faint
      stars.push({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: 0.3 + depth * 0.8,
        speed: MIN_SPEED + depth * (MAX_SPEED - MIN_SPEED),
        brightness: 0.35 + depth * 0.65,
        spriteIndex: pickWeighted(),
        twinklePhase: Math.random() * Math.PI * 2,
        twinkleHz: 0.1 + Math.random() * 0.5,
        // Only some stars shimmer noticeably; the rest stay near-steady.
        twinkleAmp: Math.random() < 0.6 ? 0.15 + Math.random() * 0.35 : 0.05,
      });
    }
    return stars;
  }, []);

  const renderStars = useCallback((ctx: CanvasRenderingContext2D, width: number, height: number, elapsed: number) => {
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.fillStyle = BACKGROUND_COLOR;
    ctx.fillRect(0, 0, width, height);

    // Additive blending so cores and haloes bloom instead of flatly stacking.
    ctx.globalCompositeOperation = 'lighter';

    const twinkle = !reducedMotionRef.current;
    const sprites = spritesRef.current;

    for (const star of starsRef.current) {
      let alpha = star.brightness;
      if (twinkle) {
        const s = Math.sin(elapsed * star.twinkleHz * Math.PI * 2 + star.twinklePhase);
        alpha *= 1 - star.twinkleAmp + star.twinkleAmp * (0.5 + 0.5 * s);
      }
      ctx.globalAlpha = Math.max(0, Math.min(1, alpha));

      // Draw the glow sprite scaled to ~4x the core radius for a soft halo.
      const drawR = star.radius * 4;
      ctx.drawImage(sprites[star.spriteIndex], star.x - drawR, star.y - drawR, drawR * 2, drawR * 2);
    }

    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }, []);

  const updateStars = useCallback((width: number, height: number, deltaMs: number) => {
    for (const star of starsRef.current) {
      star.x -= star.speed * deltaMs;
      if (star.x < -star.radius * 6) {
        star.x = width + star.radius * 6;
        star.y = Math.random() * height;
      }
    }
  }, []);

  const cssSizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  const animate = useCallback((currentTime: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rawDelta = currentTime - lastFrameTimeRef.current;
    lastFrameTimeRef.current = currentTime;
    const deltaMs = Math.min(rawDelta, MAX_DELTA_MS);
    elapsedRef.current += deltaMs / 1000;

    const { w, h } = cssSizeRef.current;
    updateStars(w, h, deltaMs);
    renderStars(ctx, w, h, elapsedRef.current);

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [updateStars, renderStars]);

  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    cssSizeRef.current = { w, h };

    // Back the canvas at device resolution for crisp stars on HiDPI displays,
    // then draw in CSS pixels via the DPR transform.
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    starsRef.current = initializeStars(w, h);

    if (reducedMotionRef.current && ctx) {
      renderStars(ctx, w, h, 0);
    }
  }, [initializeStars, renderStars]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    spritesRef.current = STAR_COLORS.map((c) => buildSprite(c.rgb));
    reducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    handleResize();
    window.addEventListener('resize', handleResize);

    if (reducedMotionRef.current) {
      return () => window.removeEventListener('resize', handleResize);
    }

    // On returning from a background tab, resync the clock so the first frame
    // has a normal-sized delta instead of one huge accumulated jump.
    const onVisibility = () => {
      if (!document.hidden) lastFrameTimeRef.current = performance.now();
    };
    document.addEventListener('visibilitychange', onVisibility);

    lastFrameTimeRef.current = performance.now();
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [animate, handleResize]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -1,
        pointerEvents: 'none',
        background: BACKGROUND_COLOR,
      }}
    />
  );
};

export default BackgroundStars;
